import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "preact/hooks";
import type {
	BrickedPictureOutput,
	ImageZoomOffset,
	Palette,
} from "@brickme/project-core/src/model/picture.ts";
import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type { BackgroundImageSource } from "@brickme/project-core/src/image-build/operation.ts";
import type { BuildWorker } from "./build-worker-bridge.ts";
import { usePicture } from "../picture-edit/picture-edit-context.tsx";

type BrickedBuildContextValue = {
	readonly brickedBuild: BrickedPictureOutput | undefined;
	// Imperatively rebuild at a live pan offset during a drag, bypassing the
	// picture context so the editor subtree doesn't re-render per move (which
	// stutters panning on phones). undefined reverts to the committed picture.
	readonly setLiveImageZoomOffset: (
		offset: ImageZoomOffset | undefined,
	) => void;
};

const brickedBuildContext = createContext<BrickedBuildContextValue | undefined>(
	undefined,
);

type PictureBuildProviderProps = {
	/** @deprecated Shouldn't need to use this. Will likely resolve with some refactoring insight */
	readonly onChange?: (brickedBuild: BrickedPictureOutput | undefined) => void;
	readonly buildWorker: BuildWorker;
	readonly palette: Palette;
	readonly backgroundImages: readonly BackgroundImageSource[];
	readonly buildSources: BuildSources;
	readonly enabled: boolean;
	readonly children: ComponentChildren;
};

function PictureBuildProvider({
	buildWorker,
	buildSources,
	onChange,
	palette,
	backgroundImages,
	enabled,
	children,
}: PictureBuildProviderProps) {
	const [brickedBuild, setBrickedBuildOutput] = useState<
		BrickedPictureOutput | undefined
	>(undefined);

	// Notify parent of changes
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	useEffect(() => {
		onChangeRef.current?.(brickedBuild);
	}, [brickedBuild]);

	// Receive bricked build from worker
	useEffect(() => {
		function onBrickedBuild(brickedBuild: BrickedPictureOutput) {
			setBrickedBuildOutput(brickedBuild);
		}
		buildWorker.addEventListener("brickedBuild", onBrickedBuild);

		return () => {
			buildWorker.removeEventListener("brickedBuild", onBrickedBuild);
		};
	}, [buildWorker]);

	// Notify worker of palette
	useEffect(() => {
		buildWorker.setPalette(palette);
		return () => {
			buildWorker.setPalette(undefined);
		};
	}, [buildWorker, palette]);

	// Notify worker of background images
	useEffect(() => {
		buildWorker.setBackgroundImages(backgroundImages);
	}, [buildWorker, backgroundImages]);

	// Notify worker of build sources
	useEffect(() => {
		buildWorker.setBuildSources(buildSources);

		return () => {
			buildWorker.setBuildSources(undefined);
		};
	}, [buildWorker, buildSources]);

	// Transfer picture to worker when changes. Pause builds when source rendering mode
	const { picture } = usePicture();
	const pictureRef = useRef(picture);
	pictureRef.current = picture;
	// Live pan offset during a drag; overrides the committed picture's offset when
	// posting to the worker, so bricks re-crop mid-drag without a subtree re-render.
	const liveOffsetRef = useRef<ImageZoomOffset | undefined>(undefined);
	useEffect(() => {
		if (enabled) {
			buildWorker.setPicture(
				liveOffsetRef.current === undefined
					? picture
					: { ...picture, imageZoomOffset: liveOffsetRef.current },
			);
		} else {
			buildWorker.clearPicture();
			// Clear build. Without this we get a flash of "old" bricked when
			// we turn back on.
			setBrickedBuildOutput(undefined);
		}
	}, [enabled, buildWorker, picture]);

	const setLiveImageZoomOffset = useCallback(
		(offset: ImageZoomOffset | undefined) => {
			liveOffsetRef.current = offset;
			buildWorker.setPicture(
				offset === undefined
					? pictureRef.current
					: { ...pictureRef.current, imageZoomOffset: offset },
			);
		},
		[buildWorker],
	);

	const contextValue = useMemo(
		(): BrickedBuildContextValue => ({ brickedBuild, setLiveImageZoomOffset }),
		[brickedBuild, setLiveImageZoomOffset],
	);

	return (
		<brickedBuildContext.Provider value={contextValue}>
			{children}
		</brickedBuildContext.Provider>
	);
}

function useCurrentBrickedBuild() {
	const context = useContext(brickedBuildContext);
	if (context === undefined) {
		throw new Error("useCurrentBrickedBuild must be used within context");
	}
	return context.brickedBuild;
}

function useSetLiveImageZoomOffset() {
	const context = useContext(brickedBuildContext);
	if (context === undefined) {
		throw new Error("useSetLiveImageZoomOffset must be used within context");
	}
	return context.setLiveImageZoomOffset;
}

export type { PictureBuildProviderProps };
export {
	PictureBuildProvider,
	useCurrentBrickedBuild,
	useSetLiveImageZoomOffset,
};
